import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import classNames from 'classnames/bind';
import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';
import { ReactComponent as TrashIcon } from '@ICONS/trash-icon.svg';
import styles from './PermissionModal.module.scss';
import { permissions } from '../DocumentPermission/types';

const cx: CX = classNames.bind(styles);

// Custom Avatar Component
function CustomAvatar(props: CustomAvatarProps) {
    const { name, className } = props;
    
    const getInitials = (fullName: string) => fullName
        .trim()
        .split(' ')
        .reduce((acc, part) => acc + part[0].toUpperCase(), '');

    return (
        <div className={cx('custom-avatar', className)}>
            {getInitials(name)}
        </div>
    );
}

CustomAvatar.defaultProps = {
    className: '',
};

CustomAvatar.propTypes = {
    name: PropTypes.string.isRequired,
    className: PropTypes.string,
};

type CustomAvatarProps = InferProps<typeof CustomAvatar.propTypes> & typeof CustomAvatar.defaultProps;

// Custom Delete Button Component  
function CustomDeleteButton(props: CustomDeleteButtonProps) {
    const { onClick, className } = props;
    
    return (
        <button
            type="button"
            className={cx('custom-delete-button', className)}
            onClick={onClick}
            aria-label="Delete user"
            title="Remove user"
        >
            <TrashIcon />
        </button>
    );
}

CustomDeleteButton.defaultProps = {
    className: '',
};

CustomDeleteButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string,
};

type CustomDeleteButtonProps = InferProps<typeof CustomDeleteButton.propTypes> & typeof CustomDeleteButton.defaultProps;

function PermissionTable(props: any) {
    const { users, handlecheckboxChange, handleDeleteUser } = props;

    if (users.length === 0) {
        return (
            <div className={cx('empty-state')}>
                <p>No users added yet. Use the search above to add users.</p>
            </div>
        );
    }

    return (
        <div className={cx('permissions-table')}>
            <div className={cx('table-header')}>
                <div className={cx('header-user-info')}>User Info</div>
                <div className={cx('header-permissions')}>Document Permissions</div>
                <div className={cx('header-actions')} />
            </div>
            
            <div className={cx('table-body')}>
                {users.map((user: any) => (
                    <div key={user.id} className={cx('user-row')}>
                        <div className={cx('user-info')}>
                            <CustomAvatar 
                                name={user.name || user.email.split('@')[0]}
                                className="user-avatar"
                            />
                            <div className={cx('user-details')}>
                                <span className={cx('user-name')}>
                                    {user.name || user.email.split('@')[0]}
                                </span>
                                <span className={cx('user-email')}>
                                    {user.email}
                                </span>
                            </div>
                        </div>
                        
                        <div className={cx('permissions-list')}>
                            {permissions.map((col) => {
                                // Map permission codenames to the boolean properties in the user object
                                const permissionMap: { [key: string]: string } = {
                                    can_generate_document: 'can_generate_document',
                                    can_edit_document: 'can_edit_document',
                                    can_delete_document: 'can_delete_document',
                                    can_publish_document: 'can_publish_document',
                                    can_manage_document_tags: 'can_manage_document_tags',
                                    can_manage_document_vendors: 'can_manage_document_vendors',
                                };
                                
                                const permissionKey = permissionMap[col.codename];
                                const checked = permissionKey ? user[permissionKey] : false;
                                
                                return (
                                    <div key={col.id} className={cx('permission-item')}>
                                        <BasicCheckbox
                                            title={col.name}
                                            checked={checked}
                                            fontSize={12}
                                            fontWeight={500}
                                            onChange={() => handlecheckboxChange(
                                                user.id,
                                                col.codename,
                                                !checked,
                                            )}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        
                        <div className={cx('user-actions')}>
                            <CustomDeleteButton 
                                onClick={() => handleDeleteUser(user.id)}
                                className="delete-button"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default PermissionTable;
